import { api } from "@/lib/apiClient";
import { listEmployeeTimes } from "@/lib/employeeTimes";
import { listHeroStundenZuordnung } from "@/lib/stundenUmbuchung";
import type { HeroCustomFieldSchema, KalkStundenAppEntry } from "@/lib/projects";
import type { HeroCustomFieldValue, Order } from "@/lib/projects";

export interface HoursUsageEntry {
  eligible: boolean;
  /** Festpreis-Ist: alle gebuchten Stunden außer Nachweis-Positionen. */
  plannedHours: number | null;
  actualHours: number | null;
  percentRaw: number | null;
  /** Angebotene Nachweisstunden aus dem App-Angebot. */
  nachweisPlannedHours?: number | null;
  /** Ist-Stunden, die auf einer Nachweis-Position gebucht sind. */
  nachweisActualHours?: number | null;
  nachweisPercentRaw?: number | null;
  loading: boolean;
  error: boolean;
}

export interface HoursUsageDisplay {
  eligible: boolean;
  loading: boolean;
  percentRaw: number | null;
  ringPercent: number;
  displayPercent: number;
  isOver: boolean;
  trafficLight: HoursUsageTrafficLight | null;
  actualHours: number | null;
  plannedHours: number | null;
  tooltip: string;
}

export type HoursUsageTrafficLight = "ok" | "warn" | "over";

export function getHoursUsageTrafficLight(
  percentRaw: number | null | undefined,
): HoursUsageTrafficLight | null {
  if (!Number.isFinite(percentRaw)) return null;
  const value = Number(percentRaw);
  if (value > 100) return "over";
  if (value >= 80) return "warn";
  return "ok";
}

export function hoursUsageStatus(
  planned: number | null,
  actual: number | null,
  traffic: HoursUsageTrafficLight | null,
  labels?: { over?: string; exhausted?: string },
): { tone: HoursUsageTrafficLight; text: string } | null {
  if (planned == null || planned <= 0 || actual == null || !Number.isFinite(actual)) {
    return null;
  }
  const remaining = Math.round((planned - actual) * 10) / 10;
  if (remaining < 0) {
    return {
      tone: "over",
      text: `${formatHours(-remaining)} ${labels?.over || "über kalkuliert"}`,
    };
  }
  if (remaining === 0) {
    return { tone: traffic ?? "ok", text: labels?.exhausted || "Kalkulation ausgeschöpft" };
  }
  return {
    tone: traffic === "warn" ? "warn" : "ok",
    text: `${formatHours(remaining)} verbleibend`,
  };
}

const ORDER_TYPE_LABELS = ["Auftragsart"];
const WORKSHOP_LABELS = ["Kalk. Werkstattstunden", "Kalk Werkstattstunden", "Werkstattstunden"];
const MONTAGE_LABELS = ["Kalk. Montagestunden", "Kalk Montagestunden", "Montagestunden"];

const EMPTY_ENTRY: HoursUsageEntry = {
  eligible: false,
  plannedHours: null,
  actualHours: null,
  percentRaw: null,
  loading: false,
  error: false,
};

function normalizeLabel(value: string): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function isFixedPriceOrderType(value: unknown): boolean {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
  return (
    normalized === "festpreis" ||
    normalized === "festpreisauftrag" ||
    normalized === "fp" ||
    normalized === "nw+fp" ||
    normalized === "nwfp"
  );
}

export function parseCustomHoursValue(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return null;

  const raw = String(value).replace(/\u00A0/g, " ").trim();
  if (!raw) return null;

  const match = raw.match(/[-+]?\d+(?:[.,]\d+)*/);
  if (!match) return null;

  let token = match[0];
  const hasComma = token.includes(",");
  const hasDot = token.includes(".");

  if (hasComma && hasDot) {
    const lastComma = token.lastIndexOf(",");
    const lastDot = token.lastIndexOf(".");
    if (lastComma > lastDot) {
      token = token.replace(/\./g, "").replace(",", ".");
    } else {
      token = token.replace(/,/g, "");
    }
  } else if (hasComma) {
    token = token.replace(",", ".");
  }

  if (!/^[-+]?\d*\.?\d+$/.test(token)) return null;
  const parsed = Number.parseFloat(token);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatHours(value: number | null | undefined): string {
  if (!Number.isFinite(value)) return "–";
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded.toFixed(1).replace(".", ",")} h`;
}

function buildLabelIndex(schemas: HeroCustomFieldSchema[]): Map<string, { schemaId: string; propertyUuid: string }> {
  const index = new Map<string, { schemaId: string; propertyUuid: string }>();
  for (const schema of schemas) {
    const schemaId = String(schema.schemaId || "");
    for (const property of schema.properties || []) {
      const label = normalizeLabel(property.label || "");
      if (!label || !schemaId || !property.uuid) continue;
      index.set(label, { schemaId, propertyUuid: String(property.uuid) });
    }
  }
  return index;
}

function readHeroCustomFieldValue(
  fields: HeroCustomFieldValue[] | null | undefined,
  mapping: { schemaId: string; propertyUuid: string } | undefined,
): unknown {
  if (!mapping) return null;
  const list = Array.isArray(fields) ? fields : [];
  const match = list.find((cf) => {
    const schemaId = String(cf.schemaId ?? cf.schema_id ?? "");
    const propertyUuid = String(cf.propertyUuid ?? cf.property_uuid ?? cf.uuid ?? "");
    return schemaId === mapping.schemaId && propertyUuid === mapping.propertyUuid;
  });
  if (!match || match.value == null || match.value === "") return null;
  return match.value;
}

export function resolveCustomFieldByLabels(
  order: Order,
  labels: string[],
  labelIndex: Map<string, { schemaId: string; propertyUuid: string }>,
): unknown {
  const search = labels.map(normalizeLabel).filter(Boolean);
  if (!search.length) return null;
  for (const label of search) {
    const mapping = labelIndex.get(label);
    const value = readHeroCustomFieldValue(order.hero_custom_fields, mapping);
    if (value != null && value !== "") return value;
  }
  return null;
}

/** True, wenn App-Angebot-Stunden die Hero-CF-Stunden ersetzen sollen. */
export function orderUsesAppKalkStunden(order: Order): boolean {
  return Boolean(order.has_app_angebot) && Array.isArray(order.kalk_stunden_app);
}

export function resolveAppKalkStunden(order: Order): KalkStundenAppEntry[] {
  if (!orderUsesAppKalkStunden(order)) return [];
  return (order.kalk_stunden_app || []).filter(
    (item) => item && Number.isFinite(Number(item.stunden)) && Number(item.stunden) > 0,
  );
}

/** Lohnarten mit Sichtbarkeit „Nur Büro“ sind für Monteure ausgeblendet. */
export function isMonteurVisibleKalkEntry(entry: KalkStundenAppEntry): boolean {
  return String(entry.sichtbarkeit || "alle").trim().toLowerCase() !== "buero";
}

function normalizeLohnartKey(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
}

function lohnartLooksLikeExtra(key: string, label: string): boolean {
  const text = `${key} ${label}`;
  return text.includes("zulage") || text.includes("zuschl");
}

function lohnartMatchesStem(key: string, label: string, stem: string): boolean {
  if (key === stem) return true;
  const text = label || key;
  return (
    text === stem ||
    text === `${stem}lohn` ||
    text === `${stem}stunden` ||
    text === `kalk${stem}stunden`
  );
}

export type KalkHoursKind = "workshop" | "montage" | "other";

/** Nur echte Werkstatt-/Montagestunden – keine Zulagen, Zuschläge, Fahrzeiten. */
export function classifyKalkLohnart(entry: Pick<KalkStundenAppEntry, "lohnart_id" | "bezeichnung">): KalkHoursKind {
  const key = normalizeLohnartKey(entry.lohnart_id);
  const label = normalizeLohnartKey(entry.bezeichnung);
  if (lohnartLooksLikeExtra(key, label)) return "other";
  if (lohnartMatchesStem(key, label, "werkstatt")) return "workshop";
  if (lohnartMatchesStem(key, label, "montage")) return "montage";
  return "other";
}

export function isKalkZuschlag(entry: Pick<KalkStundenAppEntry, "lohnart_id" | "bezeichnung">): boolean {
  return lohnartLooksLikeExtra(normalizeLohnartKey(entry.lohnart_id), normalizeLohnartKey(entry.bezeichnung));
}

export type KalkStundenStem = "werkstatt" | "montage" | null;

/** Stem auch bei Zuschlägen (Montagelohn Zuschlag → montage). */
export function resolveKalkStundenStem(
  entry: Pick<KalkStundenAppEntry, "lohnart_id" | "bezeichnung">,
): KalkStundenStem {
  const key = normalizeLohnartKey(entry.lohnart_id);
  const label = normalizeLohnartKey(entry.bezeichnung);
  const text = `${key} ${label}`;
  if (lohnartMatchesStem(key, label, "werkstatt") || text.includes("werkstatt")) return "werkstatt";
  if (lohnartMatchesStem(key, label, "montage") || text.includes("montage")) return "montage";
  return null;
}

export type KalkStundenDisplayGroupId = "einsatz" | "zuschlag" | "weitere";
export type KalkStundenSichtbarkeit = "alle" | "buero" | "mixed";

export interface KalkStundenDisplayGroup {
  id: KalkStundenDisplayGroupId;
  title: string;
  items: KalkStundenAppEntry[];
  total: number;
  sichtbarkeit: KalkStundenSichtbarkeit;
}

function groupSichtbarkeit(items: KalkStundenAppEntry[]): KalkStundenSichtbarkeit {
  let hasAlle = false;
  let hasBuero = false;
  for (const item of items) {
    if (isMonteurVisibleKalkEntry(item)) hasAlle = true;
    else hasBuero = true;
  }
  if (hasAlle && hasBuero) return "mixed";
  return hasBuero ? "buero" : "alle";
}

function sortKalkDisplayItems(items: KalkStundenAppEntry[]): KalkStundenAppEntry[] {
  const rank = (entry: KalkStundenAppEntry) => {
    const stem = resolveKalkStundenStem(entry);
    if (stem === "werkstatt") return 0;
    if (stem === "montage") return 1;
    return 2;
  };
  return [...items].sort((a, b) => {
    const delta = rank(a) - rank(b);
    if (delta !== 0) return delta;
    return String(a.bezeichnung || a.lohnart_id || "").localeCompare(
      String(b.bezeichnung || b.lohnart_id || ""),
      "de",
    );
  });
}

function sumKalkStunden(items: KalkStundenAppEntry[]): number {
  return items.reduce((sum, item) => sum + (Number(item.stunden) || 0), 0);
}

/** Einsatzstunden, Zuschläge und restliche Lohnarten getrennt für die Übersicht. */
export function groupKalkStundenForDisplay(entries: KalkStundenAppEntry[]): KalkStundenDisplayGroup[] {
  const einsatz: KalkStundenAppEntry[] = [];
  const zuschlag: KalkStundenAppEntry[] = [];
  const weitere: KalkStundenAppEntry[] = [];

  for (const entry of entries) {
    const hours = Number(entry.stunden);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    if (isKalkZuschlag(entry)) {
      zuschlag.push(entry);
      continue;
    }
    const kind = classifyKalkLohnart(entry);
    if (kind === "workshop" || kind === "montage") {
      einsatz.push(entry);
    } else {
      weitere.push(entry);
    }
  }

  const groups: KalkStundenDisplayGroup[] = [];
  if (einsatz.length) {
    groups.push({
      id: "einsatz",
      title: "Einsatzstunden",
      items: sortKalkDisplayItems(einsatz),
      total: sumKalkStunden(einsatz),
      sichtbarkeit: groupSichtbarkeit(einsatz),
    });
  }
  if (zuschlag.length) {
    groups.push({
      id: "zuschlag",
      title: "Zuschläge",
      items: sortKalkDisplayItems(zuschlag),
      total: sumKalkStunden(zuschlag),
      sichtbarkeit: groupSichtbarkeit(zuschlag),
    });
  }
  if (weitere.length) {
    const sichtbarkeit = groupSichtbarkeit(weitere);
    groups.push({
      id: "weitere",
      title: sichtbarkeit === "buero" ? "Büro" : "Weitere",
      items: sortKalkDisplayItems(weitere),
      total: sumKalkStunden(weitere),
      sichtbarkeit,
    });
  }
  return groups;
}

export function kalkStundenChipLabel(
  entry: Pick<KalkStundenAppEntry, "lohnart_id" | "bezeichnung">,
  groupId: KalkStundenDisplayGroupId,
): string {
  if (groupId === "einsatz" || groupId === "zuschlag") {
    const stem = resolveKalkStundenStem(entry);
    if (stem === "werkstatt") return "Werkstatt";
    if (stem === "montage") return "Montage";
  }
  return String(entry.bezeichnung || entry.lohnart_id || "Stunden").trim();
}

/** Werkstatt-/Montage-Stunden aus App-Kalk (per lohnart_id / Bezeichnung). */
export function splitAppKalkStunden(entries: KalkStundenAppEntry[]): {
  workshop: number | null;
  montage: number | null;
  other: KalkStundenAppEntry[];
  total: number;
} {
  let workshop = 0;
  let montage = 0;
  let hasWorkshop = false;
  let hasMontage = false;
  const other: KalkStundenAppEntry[] = [];
  for (const entry of entries) {
    const hours = Number(entry.stunden) || 0;
    if (hours <= 0) continue;
    const kind = classifyKalkLohnart(entry);
    if (kind === "workshop") {
      workshop += hours;
      hasWorkshop = true;
      continue;
    }
    if (kind === "montage") {
      montage += hours;
      hasMontage = true;
      continue;
    }
    other.push(entry);
  }
  return {
    workshop: hasWorkshop ? workshop : null,
    montage: hasMontage ? montage : null,
    other,
    total: workshop + montage,
  };
}

function plannedNachweisHours(order: Order): number | null {
  const value = Number(order.nachweis_stunden_app);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function computeHoursUsageMeta(
  order: Order,
  labelIndex: Map<string, { schemaId: string; propertyUuid: string }>,
): Omit<HoursUsageEntry, "loading" | "error"> {
  const appOrderType = String(order.auftragsart_app || "").trim();
  const orderTypeValue =
    appOrderType || resolveCustomFieldByLabels(order, ORDER_TYPE_LABELS, labelIndex);
  const eligible = isFixedPriceOrderType(orderTypeValue);
  const nachweisPlannedHours = plannedNachweisHours(order);

  if (orderUsesAppKalkStunden(order)) {
    const split = splitAppKalkStunden(resolveAppKalkStunden(order));
    return {
      eligible,
      plannedHours: split.total > 0 ? split.total : null, // nur Werkstatt + Montage
      actualHours: null,
      percentRaw: null,
      nachweisPlannedHours,
      nachweisActualHours: null,
      nachweisPercentRaw: null,
    };
  }

  const workshopValue = parseCustomHoursValue(
    resolveCustomFieldByLabels(order, WORKSHOP_LABELS, labelIndex),
  );
  const montageValue = parseCustomHoursValue(
    resolveCustomFieldByLabels(order, MONTAGE_LABELS, labelIndex),
  );
  const plannedHours = (workshopValue || 0) + (montageValue || 0);
  return {
    eligible,
    plannedHours: plannedHours > 0 ? plannedHours : null,
    actualHours: null,
    percentRaw: null,
    nachweisPlannedHours,
    nachweisActualHours: null,
    nachweisPercentRaw: null,
  };
}

/** Geplante Werkstatt-/Montagestunden: App-Angebot hat Vorrang vor Hero-CF. */
export function resolvePlannedWorkshopMontage(
  order: Order,
  labelIndex: Map<string, { schemaId: string; propertyUuid: string }>,
  options?: { monteurVisibleOnly?: boolean },
): { workshop: number | null; montage: number | null; other: KalkStundenAppEntry[] } {
  if (orderUsesAppKalkStunden(order)) {
    let entries = resolveAppKalkStunden(order);
    if (options?.monteurVisibleOnly) {
      entries = entries.filter(isMonteurVisibleKalkEntry);
    }
    const split = splitAppKalkStunden(entries);
    return { workshop: split.workshop, montage: split.montage, other: split.other };
  }
  return {
    workshop: parseCustomHoursValue(
      resolveCustomFieldByLabels(order, WORKSHOP_LABELS, labelIndex),
    ),
    montage: parseCustomHoursValue(
      resolveCustomFieldByLabels(order, MONTAGE_LABELS, labelIndex),
    ),
    other: [],
  };
}

interface HeroTimeEntry {
  category_name?: string | null;
  start?: string | null;
  duration_in_seconds?: number | null;
}

function sumHeroTimeSeconds(items: HeroTimeEntry[]): number {
  let total = 0;
  for (const it of items) {
    const cat = String(it.category_name || "").toLowerCase();
    if (cat.includes("pause")) continue;
    if (!it.start) continue;
    const d = new Date(it.start);
    if (Number.isNaN(d.getTime())) continue;
    if (typeof it.duration_in_seconds === "number") {
      total += it.duration_in_seconds;
    }
  }
  return total;
}

interface SubcontractorTimeRow {
  hours_decimal?: number | string | null;
}

/** App-Zeiten nach Preisart. Nachweis-Positionen zählen nicht in den Festpreis. */
export function sumEmployeeHoursByPreisart(
  entries: Array<{
    entry_type?: string | null;
    hours_decimal?: number | null;
    position_preisart?: string | null;
  }>,
): { festpreis: number; nachweis: number } {
  let festpreis = 0;
  let nachweis = 0;
  for (const entry of entries) {
    if (entry.entry_type === "break" || entry.entry_type === "hero") continue;
    const value = Number(entry.hours_decimal);
    if (!Number.isFinite(value) || value <= 0) continue;
    if (String(entry.position_preisart || "").trim().toLowerCase() === "nachweis") {
      nachweis += value;
    } else {
      festpreis += value;
    }
  }
  return { festpreis, nachweis };
}

export interface OrderActualHours {
  /** Summe aus Hero, Nachunternehmer und App-Zeiten, inklusive Nachweis. */
  actualHours: number | null;
  /** Anteil der App-Zeiten auf Nachweis-Positionen. */
  nachweisHours: number | null;
  error: boolean;
}

const actualHoursPromises = new Map<number, Promise<OrderActualHours>>();

export async function fetchOrderActualHours(order: Order): Promise<OrderActualHours> {
  const orderId = order.id;
  if (!Number.isFinite(orderId)) return { actualHours: null, nachweisHours: null, error: true };

  const cached = actualHoursPromises.get(orderId);
  if (cached) return cached;

  const promise = (async () => {
    let heroSeconds = 0;
    let heroError = false;
    const heroProjectId = order.hero_project_id;
    if (heroProjectId) {
      try {
        const data = await api.get<HeroTimeEntry[]>(
          `/auftraege/times/${encodeURIComponent(String(heroProjectId))}`,
        );
        heroSeconds = sumHeroTimeSeconds(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("[hoursUsage] Hero-Zeiten laden fehlgeschlagen:", err);
        heroError = true;
      }
    }

    if (!heroError) {
      const reassigned = await listHeroStundenZuordnung(orderId);
      heroSeconds += sumHeroTimeSeconds(
        reassigned.map((item) => ({
          category_name: item.category_name ?? null,
          start: item.start ?? null,
          duration_in_seconds:
            typeof item.duration_in_seconds === "number" ? item.duration_in_seconds : null,
        })),
      );
    }

    let subcontractorHours = 0;
    let subcontractorError = false;
    try {
      const data = await api.get<SubcontractorTimeRow[]>(
        `/auftraege/${encodeURIComponent(String(orderId))}/subcontractor-times?include_all=true`,
      );
      const list = Array.isArray(data) ? data : [];
      subcontractorHours = list.reduce((sum, item) => {
        const value = parseCustomHoursValue(item?.hours_decimal);
        return sum + (value || 0);
      }, 0);
    } catch (err) {
      console.warn("[hoursUsage] Nachunternehmer-Zeiten laden fehlgeschlagen:", err);
      subcontractorError = true;
    }

    let employeeFestpreis = 0;
    let employeeNachweis = 0;
    let employeeError = false;
    try {
      const entries = await listEmployeeTimes({ auftrag_id: orderId });
      const split = sumEmployeeHoursByPreisart(entries);
      employeeFestpreis = split.festpreis;
      employeeNachweis = split.nachweis;
    } catch (err) {
      console.warn("[hoursUsage] App-Zeiten laden fehlgeschlagen:", err);
      employeeError = true;
    }

    const hasError = heroError || subcontractorError || employeeError;
    const actualHours = hasError
      ? null
      : heroSeconds / 3600 + subcontractorHours + employeeFestpreis + employeeNachweis;
    return {
      actualHours,
      nachweisHours: hasError ? null : employeeNachweis,
      error: hasError,
    };
  })();

  actualHoursPromises.set(orderId, promise);
  try {
    return await promise;
  } finally {
    actualHoursPromises.delete(orderId);
  }
}

interface HoursUsageBatchEntry {
  actual_hours?: number | null;
  nachweis_hours?: number | null;
  error?: boolean;
}

const HOURS_USAGE_BATCH_CHUNK = 500;

/**
 * Lädt den Ist-Stundenverbrauch (Hero, App-Zeiten, Nachunternehmer) für
 * mehrere Aufträge. Liefert eine Map auftrag_id → { actualHours, nachweisHours, error }.
 * `actualHours` bleibt die Gesamtsumme; `nachweisHours` ist der Nachweis-Anteil.
 */
export async function fetchActualHoursByOrderIds(
  orderIds: number[],
): Promise<Map<number, OrderActualHours>> {
  const result = new Map<number, OrderActualHours>();
  const ids = Array.from(new Set(orderIds.filter((id) => Number.isFinite(id) && id > 0)));
  if (!ids.length) return result;

  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += HOURS_USAGE_BATCH_CHUNK) {
    chunks.push(ids.slice(i, i + HOURS_USAGE_BATCH_CHUNK));
  }

  await Promise.all(
    chunks.map(async (slice) => {
      try {
        const data = await api.get<Record<string, HoursUsageBatchEntry>>(
          `/auftraege/hours-usage/batch?order_ids=${encodeURIComponent(slice.join(","))}`,
        );
        for (const [key, value] of Object.entries(data || {})) {
          const id = Number(key);
          if (!Number.isFinite(id)) continue;
          const errored = Boolean(value?.error);
          const actual = value?.actual_hours;
          const nachweis = value?.nachweis_hours;
          result.set(id, {
            actualHours:
              !errored && typeof actual === "number" && Number.isFinite(actual) ? actual : null,
            nachweisHours:
              errored
                ? null
                : typeof nachweis === "number" && Number.isFinite(nachweis)
                  ? nachweis
                  : 0,
            error: errored,
          });
        }
      } catch (err) {
        console.warn("[hoursUsage] Batch-Stunden laden fehlgeschlagen:", err);
        for (const id of slice) {
          result.set(id, { actualHours: null, nachweisHours: null, error: true });
        }
      }
    }),
  );

  return result;
}

/**
 * Lädt den Ist-Stundenverbrauch für mehrere Aufträge in einem (bzw. wenigen)
 * Backend-Aufruf(en) statt 2×N Einzelrequests. Liefert eine Map order.id →
 * { actualHours, nachweisHours, error }.
 */
export async function fetchActualHoursBatch(
  orders: Order[],
): Promise<Map<number, OrderActualHours>> {
  return fetchActualHoursByOrderIds(orders.map((order) => order.id));
}

export function buildLabelIndexFromSchemas(schemas: HeroCustomFieldSchema[]): Map<string, { schemaId: string; propertyUuid: string }> {
  return buildLabelIndex(schemas);
}

export function emptyHoursUsageEntry(): HoursUsageEntry {
  return { ...EMPTY_ENTRY };
}

export function finalizeHoursUsageEntry(
  meta: Omit<HoursUsageEntry, "loading" | "error">,
  actualHours: number | null,
  error: boolean,
  nachweisHours: number | null = null,
): HoursUsageEntry {
  const nachweisActual = error
    ? null
    : Number.isFinite(nachweisHours)
      ? Math.max(0, Number(nachweisHours))
      : 0;
  const festpreisActual =
    error || !Number.isFinite(actualHours)
      ? null
      : Math.max(0, Number(actualHours) - (nachweisActual ?? 0));
  const plannedHours = Number(meta.plannedHours) || 0;
  const percentRaw =
    !error && meta.eligible && plannedHours > 0 && festpreisActual != null
      ? Math.round((festpreisActual / plannedHours) * 100)
      : null;
  const nachweisPlanned = Number(meta.nachweisPlannedHours) || 0;
  const nachweisPercentRaw =
    !error && nachweisPlanned > 0 && nachweisActual != null
      ? Math.round((nachweisActual / nachweisPlanned) * 100)
      : null;
  return {
    ...meta,
    actualHours: festpreisActual,
    percentRaw,
    nachweisActualHours: nachweisActual,
    nachweisPercentRaw,
    loading: false,
    error,
  };
}

export function toHoursUsageDisplay(entry: HoursUsageEntry | undefined): HoursUsageDisplay | null {
  if (!entry?.eligible) return null;
  const plannedHours = entry.plannedHours;
  if (plannedHours == null || !Number.isFinite(plannedHours) || plannedHours <= 0) return null;

  if (entry.loading || !Number.isFinite(entry.actualHours)) {
    return {
      eligible: true,
      loading: true,
      percentRaw: null,
      ringPercent: 0,
      displayPercent: 0,
      isOver: false,
      trafficLight: null,
      actualHours: null,
      plannedHours,
      tooltip: "Stundenverbrauch wird geladen …",
    };
  }

  const actualHours = entry.actualHours;
  const percentRaw = Number.isFinite(entry.percentRaw) ? Number(entry.percentRaw) : 0;
  const displayPercent = Math.max(0, Math.min(999, Math.round(percentRaw)));
  const ringPercent = Math.max(0, Math.min(100, percentRaw));
  const actualLabel = formatHours(actualHours);
  const plannedLabel = formatHours(plannedHours);
  const hasNachweis =
    Number(entry.nachweisActualHours) > 0 || Number(entry.nachweisPlannedHours) > 0;
  const tooltip = hasNachweis
    ? `Festpreis: Ist ${actualLabel} / Kalk. ${plannedLabel} · Nachweis: ${formatHours(entry.nachweisActualHours)} / ${formatHours(entry.nachweisPlannedHours)}`
    : `Ist: ${actualLabel} / Soll: ${plannedLabel}`;
  const trafficLight = getHoursUsageTrafficLight(percentRaw);

  return {
    eligible: true,
    loading: false,
    percentRaw,
    ringPercent,
    displayPercent,
    isOver: percentRaw > 100,
    trafficLight,
    actualHours,
    plannedHours,
    tooltip,
  };
}
